import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Breadcrumb from "@/components/ui/breadcrumb";
import LayoutSettings from "@/components/ui/layout-settings";
import { useWidgetLayout } from "@/hooks/useWidgetLayout";
import styles from "./ReportDetailLayout.module.scss";

interface ReportDetailLayoutProps {
  title: string;
  description?: string;
  categoryLabel?: string;
  exportSlot?: React.ReactNode;
  filterSlot?: React.ReactNode;
  /** Filtre çubuğunun hemen altındaki kayıtlı filtre setleri şeridi. Blok
   *  sayılmaz, sırası değişmez. */
  presetSlot?: React.ReactNode;
  /** Sayfa üstünde açılan modal(lar) — blok sayılmaz. */
  modalSlot?: React.ReactNode;
  /**
   * Verildiğinde sayfa blokları (gösterge düzeni) kullanıcı tarafından
   * gizlenebilir ve sıralanabilir olur — R11.
   *
   * Blok = bu bileşene verilen her bir üst seviye çocuk eleman (özet kartlar,
   * grafik, tablo...). Düzen tek prop ile açıldığı için 20+ rapor sayfasında
   * JSX'i yeniden kurmaya gerek kalmıyor.
   */
  layoutKey?: string;
  children: React.ReactNode;
}

/** Bilinen blok bileşenleri için okunabilir ad; bilinmeyenler "Bölüm N" olur. */
function blockLabel(name: string, index: number, t: (key: string) => string): string {
  if (name.includes("Chart")) return t("reportSettings.blockChart");
  if (name.includes("Table")) return t("reportSettings.blockTable");
  if (name.includes("Kpi") || name.includes("KPI")) return t("reportSettings.blockKpi");
  // KPI kartları çoğu sayfada düz bir <div> içinde; ilk blok pratikte özet kartlar.
  if (index === 0) return t("reportSettings.blockKpi");
  return `${t("reportSettings.blockSection")} ${index + 1}`;
}

const ReportDetailLayout: React.FC<ReportDetailLayoutProps> = ({
  title,
  description,
  categoryLabel,
  exportSlot,
  filterSlot,
  presetSlot,
  modalSlot,
  layoutKey,
  children,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const items = [
    {
      label: "Raporlar",
      onClick: () => navigate("/dashboard/reports"),
    },
    ...(categoryLabel ? [{ label: categoryLabel }] : []),
    { label: title },
  ];

  // Çocukları bloklara çevir. Anahtar bileşen adı + sırasından türetilir;
  // sayfanın yapısı değişmedikçe kararlıdır.
  const blocks = useMemo(() => {
    const list: { key: string; label: string; node: React.ReactNode }[] = [];
    const seen = new Map<string, number>();

    React.Children.toArray(children).forEach((child, index) => {
      if (!React.isValidElement(child)) return;
      const type = child.type as { displayName?: string; name?: string } | string;
      const name = typeof type === "string" ? type : (type?.displayName ?? type?.name ?? "Block");
      const occurrence = seen.get(name) ?? 0;
      seen.set(name, occurrence + 1);

      list.push({
        key: `${name}-${occurrence}`,
        label: blockLabel(name, index, t),
        node: child,
      });
    });
    return list;
  }, [children, t]);

  const widgetDefs = useMemo(() => blocks.map((b) => ({ key: b.key, label: b.label })), [blocks]);
  const layout = useWidgetLayout(layoutKey ?? "", widgetDefs);

  const orderedChildren = useMemo(() => {
    if (!layoutKey) return children;
    const byKey = new Map(blocks.map((b) => [b.key, b]));
    return layout.visibleWidgets
      .map((key) => byKey.get(key))
      .filter((b): b is (typeof blocks)[number] => !!b)
      .map((b) => <React.Fragment key={b.key}>{b.node}</React.Fragment>);
  }, [layoutKey, children, blocks, layout.visibleWidgets]);

  return (
    <DashboardLayout>
      <Header title={title} />
      <div className={styles.topBar}>
        <Breadcrumb items={items} />
        <div className={styles.slotRow}>
          {layoutKey && (
            <LayoutSettings
              buttonLabel={t("reportSettings.layoutButton")}
              panelTitle={t("reportSettings.layoutTitle")}
              hint={t("reportSettings.layoutHint", {
                visible: layout.visibleWidgets.length,
                total: layout.descriptors.length,
              })}
              items={layout.descriptors}
              isSaving={layout.isSaving}
              isCustomized={layout.isCustomized}
              onToggle={layout.toggleWidget}
              onMove={layout.moveWidget}
              onReset={layout.reset}
            />
          )}
          {exportSlot && <div className={styles.exportSlot}>{exportSlot}</div>}
        </div>
      </div>
      <div className={styles.titleRow}>
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>{title}</h2>
          {description && <p className={styles.description}>{description}</p>}
        </div>
      </div>
      {filterSlot && <div className={styles.filterSlot}>{filterSlot}</div>}
      {presetSlot && <div className={styles.presetSlot}>{presetSlot}</div>}
      <div className={styles.content}>{orderedChildren}</div>
      {modalSlot}
    </DashboardLayout>
  );
};

export default ReportDetailLayout;
